/**
 * Broadcast Middleware - Redux Middleware for Multi-Tab Sync
 *
 * Intercepts specific Redux actions and broadcasts them to other tabs.
 * Prevents infinite loops by checking meta.fromBroadcast flag.
 *
 * Usage:
 *   Add to Redux store middleware chain in store.js
 */

import { broadcastChannel } from '../../services/broadcastChannelService';

/**
 * Actions that should be broadcasted to other tabs
 * Only actions that modify cached data should be included
 *
 * ⚠️ CORRECTED: Action names match actual Redux slice implementations
 */
const BROADCASTABLE_ACTIONS = [
  // Operations
  'operations/add',
  'operations/updateOp',
  'operations/removeOp',
  'operations/setOperations',
  'operations/updateNote',
  'expOperations/removeExpOp',

  // Lots (batches)
  'lots/add',
  'lots/update',
  'lots/removeL',
  'lots/setLots',

  // Tanks
  'tanks/add',
  'tanks/update',
  'tanks/removeT',
  'tanks/setTanks',

  // Drugs (substances/drug batches)
  'drugs/addDrugItem',
  'drugs/updateDrugItem',
  'drugs/removeDrugItem',
  'drugs/setDrugs',

  // Analysis Results (storedAnalysisResults)
  'storedAnalysisResults/addStoredAnalysisResults',
  'storedAnalysisResults/setStoredAnalysisResults',
  'storedAnalysisResults/remove',

  // Warehouse
  'warehouse/addWarehouse',
  'warehouse/updateWarehouse',
  'warehouse/removeWarehouse',
  'warehouse/setWarehouse',
  'warehouse/updateNote',
];

/**
 * Redux middleware to broadcast actions to other tabs
 */
export const broadcastMiddleware = store => next => action => {
  // Skip if action came from another tab (prevent infinite loop)
  if (action.meta?.fromBroadcast) {
    return next(action);
  }

  // Execute action locally first
  const result = next(action);

  // Broadcast if action is in whitelist
  if (BROADCASTABLE_ACTIONS.includes(action.type)) {
    // Add metadata to prevent re-broadcasting
    const broadcastAction = {
      ...action,
      meta: {
        ...action.meta,
        fromBroadcast: true, // Critical flag
      },
    };

    broadcastChannel.broadcastAction(broadcastAction);

    // Debug logging (remove in production)
    if (process.env.NODE_ENV === 'development') {
      console.log('📡 Broadcasted action:', action.type);
    }
  }

  return result;
};

export default broadcastMiddleware;
